import { useState, useEffect, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { QUERY_FIND_RECORD_HEADERS_WITH_FILTERING } from 'graphql/queries/bills';
import {
    IGetRecordHeadersFiltered,
    FindRecordHeaderWithFilteringVariables,
    IRecordHeaders,
    FilterRecordHeaderInput,
    FilterAndSortAdditionalFields,
    filterOperatorEnum,
    FilterStringCriteriaEnum
} from 'ui-component/records/types';

export interface RecordOption {
    id: string;
    record: IRecordHeaders;
}

const LIMIT = 10;

export const useLazyRecordValues = (recordTypeId: number, fieldTargetId?: string) => {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState<RecordOption[]>([]);
    const [value, setValue] = useState<RecordOption | null>(null);
    const [allValues, setAllValues] = useState<RecordOption[] | null>(null);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [isSearching, setIsSearching] = useState(false);
    const [pagesFetched, setPagesFetched] = useState<Set<number>>(new Set());
    const [fetchRecords, { loading: loadingPaginated }] = useLazyQuery<IGetRecordHeadersFiltered, FindRecordHeaderWithFilteringVariables>(
        QUERY_FIND_RECORD_HEADERS_WITH_FILTERING,
        {
            fetchPolicy: 'no-cache'
        }
    );

    // Get label for a record
    const getRecordLabel = (record: any, innerFieldTargetId?: string): string => {
        const recordTypeName = record?.recordType?.name || '';
        let fieldValue = record.id; // Default to record.id

        if (innerFieldTargetId && record?.additionalFields) {
            // Find the field where the last part of tag (split by ;) matches innerFieldTargetId
            const targetField = record.additionalFields.find((field: any) => {
                if (!field.tag) return false;
                const tagParts = field.tag.split(';');
                const lastPart = tagParts[tagParts.length - 1];
                return lastPart === innerFieldTargetId;
            });
            if (targetField) {
                fieldValue = targetField.value || record.id;
            }
        }

        return `${recordTypeName} - ${fieldValue}`;
    };

    // Transform records to options format
    const transformRecordsToOptions = (records: any[]): RecordOption[] =>
        records
            .filter((record) => record?.recordType?.name)
            .map((record) => ({
                id: record.id,
                record
            }));

    // Create filter for backend search based on innnerFieldTargetId
    const createSearchFilter = (searchValue: string, innnerFieldTargetId?: string): FilterRecordHeaderInput | undefined => {
        if (!searchValue || !innnerFieldTargetId) return undefined;

        const fieldId = Number(innnerFieldTargetId);
        if (isNaN(fieldId)) return undefined;

        // Create filter structure matching what generateFilters produces for string types
        // The type definition has a mismatch, but this is what the backend expects
        return {
            additionalFields: [
                {
                    recordAdditionalFieldByTypeId: fieldId,
                    filters: [
                        {
                            logicOperator: filterOperatorEnum.AND,
                            priority: 0,
                            filterCriteria: FilterStringCriteriaEnum.contains as any, // Type mismatch in codebase
                            value: searchValue
                        }
                    ],
                    sort: undefined
                } as unknown as FilterAndSortAdditionalFields
            ]
        };
    };

    // Debounce search to avoid too many requests
    const searchTimeoutRef = useRef<NodeJS.Timeout>();
    const lastSearchValueRef = useRef<string>('');

    useEffect(() => {
        // Clear any pending search
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (inputValue.length > 0) {
            // Debounce search by 300ms
            searchTimeoutRef.current = setTimeout(() => {
                if (lastSearchValueRef.current === inputValue) return; // Skip if same value
                lastSearchValueRef.current = inputValue;

                setIsSearching(true);
                // Reset pagination state for new search
                setPage(0);
                setPagesFetched(new Set());
                setHasMore(true);

                const tenantId = Number(localStorage.getItem('tenant_id'));
                const filters = createSearchFilter(inputValue, fieldTargetId);

                fetchRecords({
                    variables: {
                        data: {
                            tenantId,
                            recordTypeId,
                            enabled: true,
                            filters
                        },
                        pagination: {
                            offset: 0,
                            limit: 100 // Load more for search
                        }
                    }
                })
                    .then(({ data }) => {
                        if (data?.filterAndSortRecordHeader?.records) {
                            const transformedOptions = transformRecordsToOptions(data.filterAndSortRecordHeader.records);
                            setOptions(transformedOptions);
                            // Don't set allValues during search to allow new searches
                            const total = data.filterAndSortRecordHeader.total || 0;
                            if (transformedOptions.length >= total) setHasMore(false);
                        }
                        setIsSearching(false);
                    })
                    .catch(() => {
                        setIsSearching(false);
                    });
            }, 300);
        } else {
            // Input is cleared - reset to show all (will be loaded via pagination)
            setOptions([]);
            setAllValues(null);
            lastSearchValueRef.current = '';
            setPage(0);
            setPagesFetched(new Set());
            setHasMore(true);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [inputValue, recordTypeId, fetchRecords, fieldTargetId]);

    // Load paginated values when dropdown opens (initial load, only when not searching)
    useEffect(() => {
        if (!isSearching && !inputValue && open && hasMore && !pagesFetched.has(page) && recordTypeId) {
            const tenantId = Number(localStorage.getItem('tenant_id'));
            fetchRecords({
                variables: {
                    data: {
                        tenantId,
                        recordTypeId,
                        enabled: true
                    },
                    pagination: {
                        offset: page * LIMIT,
                        limit: LIMIT
                    }
                }
            }).then(({ data }) => {
                if (data?.filterAndSortRecordHeader?.records) {
                    const newOptions = transformRecordsToOptions(data.filterAndSortRecordHeader.records);
                    setOptions((prev) => [...prev, ...newOptions]);
                    setPagesFetched((prev) => new Set(prev).add(page));
                    const total = data.filterAndSortRecordHeader.total || 0;
                    if ((page + 1) * LIMIT >= total) setHasMore(false);
                }
            });
        }
    }, [page, open, isSearching, fetchRecords, hasMore, recordTypeId, pagesFetched, inputValue]);

    // Function to search for a specific value (used when selectedId is provided)
    const searchByValue = (searchValue: string) => {
        if (!searchValue || !fieldTargetId) return;
        setIsSearching(true);
        setPage(0);
        setPagesFetched(new Set());
        setHasMore(true);

        const tenantId = Number(localStorage.getItem('tenant_id'));
        const filters = createSearchFilter(searchValue, fieldTargetId);
        fetchRecords({
            variables: {
                data: {
                    tenantId,
                    recordTypeId,
                    enabled: true,
                    filters
                },
                pagination: {
                    offset: 0,
                    limit: 100
                }
            }
        })
            .then(({ data }) => {
                if (data?.filterAndSortRecordHeader?.records) {
                    const transformedOptions = transformRecordsToOptions(data.filterAndSortRecordHeader.records);
                    setOptions(transformedOptions);
                    const total = data.filterAndSortRecordHeader.total || 0;
                    if (transformedOptions.length >= total) setHasMore(false);
                }
                setIsSearching(false);
            })
            .catch(() => {
                setIsSearching(false);
            });
    };

    // Export getRecordLabel for use in component
    return {
        open,
        setOpen,
        inputValue,
        setInputValue,
        options,
        setOptions,
        value,
        setValue,
        allValues,
        isSearching,
        loadingPaginated,
        page,
        setPage,
        hasMore,
        setAllValues,
        getRecordLabel: (record: any) => getRecordLabel(record, fieldTargetId),
        searchByValue
    };
};
